import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './NBATeam.css';
import CourtVisualization from './components/CourtVisualization';

function NBATeam() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('Overall');
  const [team, setTeam] = useState([]);
  const [modelStats, setModelStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playersByPosition, setPlayersByPosition] = useState({});
  const [expandedPosition, setExpandedPosition] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const API_URL = 'https://imdb-sentiment-backend.onrender.com/api/nba';
  const positions = ['PG', 'SG', 'SF', 'PF', 'C'];

  useEffect(() => {
    fetchModelStats();
    fetchTeam(mode);
    fetchAllPlayers(mode);
  }, [mode]);

  const fetchModelStats = async () => {
    try {
      const res = await fetch(`${API_URL}/model-stats`);
      const data = await res.json();
      setModelStats(data);
    } catch (err) {
      console.error('Error fetching model stats:', err);
    }
  };

  const fetchTeam = async (teamMode) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/team?mode=${teamMode}`);
      if (!res.ok) throw new Error('Failed to fetch team');
      const data = await res.json();
      setTeam(data.team);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPlayers = async (teamMode) => {
    try {
      const playerData = {};
      for (const position of positions) {
        const res = await fetch(`${API_URL}/players?position=${position}&mode=${teamMode}&limit=20`);
        if (res.ok) {
          const data = await res.json();
          playerData[position] = data.players;
        }
      }
      setPlayersByPosition(playerData);
    } catch (err) {
      console.error('Error fetching players:', err);
    }
  };

  const togglePosition = (position) => {
    setExpandedPosition(expandedPosition === position ? null : position);
  };

  const getModeDescription = () => {
    if (mode === 'Offense') {
      return 'Offense favors high PTS/AST/TS% with responsibility (USG%) and extra chances (OREB%).';
    } else if (mode === 'Defense') {
      return 'Defense favors DREB% and size proxies (height/weight) as rim/board impact.';
    } else {
      return 'Overall blends offense & defense (via net rating, PTS/AST/REB, TS%, usage, rebounding).';
    }
  };

  const getPositionColor = (pos) => {
    const colors = {
      'PG': '#3B82F6',  // blue
      'SG': '#10B981',  // green
      'SF': '#F59E0B',  // amber
      'PF': '#EF4444',  // red
      'C': '#8B5CF6'    // purple
    };
    return colors[pos] || '#6B7280';
  };

  const getPositionName = (pos) => {
    const names = {
      'PG': 'Point Guard',
      'SG': 'Shooting Guard',
      'SF': 'Small Forward',
      'PF': 'Power Forward',
      'C': 'Center'
    };
    return names[pos] || pos;
  };

  return (
    <>
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2>How This AI Works</h2>

            <div className="explanation-section">
              <h3>Technical Explanation</h3>
              <p>
                <strong>Step 1:</strong> Collect NBA player statistics including points, rebounds, assists, and other performance metrics.
                <strong> Step 2:</strong> Normalize the features to ensure all statistics are on the same scale.
                <strong> Step 3:</strong> Build a Multi-Layer Perceptron (MLP) neural network from scratch using NumPy with an input layer (player stats), hidden layers with ReLU activation, and an output layer predicting team quality scores.
                <strong> Step 4:</strong> Generate training data by creating random team combinations and labeling them with performance scores.
                <strong> Step 5:</strong> Train the network using backpropagation and gradient descent to minimize prediction error.
                <strong> Step 6:</strong> Implement position constraints to ensure each team has exactly 1 PG, 1 SG, 1 SF, 1 PF, and 1 C.
                <strong> Step 7:</strong> Deploy the model to evaluate and optimize team combinations through the Flask API.
              </p>
            </div>

            <div className="explanation-section simply-put">
              <h3>Simply Put</h3>
              <p>
                The AI learned what makes a great basketball team by practicing with thousands of different player combinations. It figured out which types of players work well together - like having a good shooter, a strong defender, and a talented playmaker. Now it can instantly pick the best 5 players for a team, making sure to choose one player for each position, just like building the perfect starting lineup.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="nba-container">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Back to Portfolio
        </button>
        <button className="info-btn" onClick={() => setShowModal(true)}>
          ℹ️ How It Works
        </button>

        <header className="nba-header">
          <h1>Optimal NBA Team Selector</h1>
          {modelStats && (
            <p className="season-label">{modelStats.season_label}</p>
          )}
        </header>

      {/* Mode Selector */}
      <div className="mode-selector">
        <div className="mode-buttons">
          {['Overall', 'Offense', 'Defense'].map((m) => (
            <button
              key={m}
              className={`mode-btn ${mode === m ? 'active' : ''}`}
              onClick={() => setMode(m)}
            >
              {m}
            </button>
          ))}
        </div>
        <p className="mode-description">{getModeDescription()}</p>
      </div>

      {/* Model Stats */}
      {modelStats && (
        <div className="model-stats-card">
          <h3>Model Performance</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Test Accuracy</span>
              <span className="stat-value">{modelStats.test_accuracy}%</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Architecture</span>
              <span className="stat-value">{modelStats.architecture}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Players</span>
              <span className="stat-value">{modelStats.total_players}</span>
            </div>
          </div>
        </div>
      )}

      {/* Loading/Error States */}
      {loading && <div className="loading">Loading optimal team...</div>}
      {error && <div className="error">Error: {error}</div>}

      {/* Available Players by Position */}
      {!loading && !error && (
        <div className="available-players-section">
          <h2>Available Players (Ranked by {mode} Score)</h2>
          <p className="section-subtitle">Click on a position to see all available players</p>

          <div className="position-accordion">
            {positions.map((position) => (
              <div key={position} className="position-section">
                <div
                  className={`position-header ${expandedPosition === position ? 'expanded' : ''}`}
                  onClick={() => togglePosition(position)}
                  style={{ borderLeftColor: getPositionColor(position) }}
                >
                  <span
                    className="position-badge"
                    style={{ backgroundColor: getPositionColor(position) }}
                  >
                    {position}
                  </span>
                  <span className="position-title">{getPositionName(position)}</span>
                  <span className="player-count">
                    {playersByPosition[position]?.length || 0} players
                  </span>
                  <span className="expand-icon">
                    {expandedPosition === position ? '▲' : '▼'}
                  </span>
                </div>

                {expandedPosition === position && playersByPosition[position] && (
                  <div className="position-players">
                    <table className="players-table">
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Player</th>
                          <th>Team</th>
                          <th>PTS</th>
                          <th>AST</th>
                          <th>REB</th>
                          <th>TS%</th>
                          <th>Confidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {playersByPosition[position].map((player, index) => (
                          <tr key={index} className={player.prediction === 1 ? 'predicted-optimal' : ''}>
                            <td>{index + 1}</td>
                            <td className="player-name-cell">{player.player_name}</td>
                            <td>{player.team_abbreviation}</td>
                            <td>{player.pts}</td>
                            <td>{player.ast}</td>
                            <td>{player.reb}</td>
                            <td>{(player.ts_pct * 100).toFixed(1)}%</td>
                            <td>{(player.pred_proba * 100).toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Optimal Team */}
      {!loading && !error && team.length > 0 && (
        <>
          <div className="team-section">
            <h2>Optimal {mode} Team</h2>
            <div className="team-grid">
              {team.map((player, index) => (
                <div
                  key={index}
                  className="player-card"
                  style={{ borderLeftColor: getPositionColor(player.position_assigned) }}
                >
                  <div className="player-header">
                    <span
                      className="position-badge"
                      style={{ backgroundColor: getPositionColor(player.position_assigned) }}
                    >
                      {player.position_assigned}
                    </span>
                    <h3>{player.player_name}</h3>
                  </div>
                  <div className="player-team">{player.team_abbreviation}</div>

                  <div className="player-stats">
                    <div className="stat-row">
                      <span>PTS</span>
                      <strong>{player.pts}</strong>
                    </div>
                    <div className="stat-row">
                      <span>AST</span>
                      <strong>{player.ast}</strong>
                    </div>
                    <div className="stat-row">
                      <span>REB</span>
                      <strong>{player.reb}</strong>
                    </div>
                    <div className="stat-row">
                      <span>TS%</span>
                      <strong>{(player.ts_pct * 100).toFixed(1)}%</strong>
                    </div>
                  </div>

                  <div className="player-footer">
                    <div className="confidence">
                      Confidence: {(player.pred_proba * 100).toFixed(1)}%
                    </div>
                    {player.position_note && (
                      <div className="position-note">{player.position_note}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Court Visualization */}
          <div className="court-section">
            <h2>Court Visualization</h2>
            <div className="court-canvas-container">
              <CourtVisualization team={team} />
            </div>
          </div>

          {/* How It Works */}
          <div className="info-section">
            <h2>How the ANN Works</h2>
            <div className="info-content">
              <h3>Neural Network Architecture</h3>
              <p><strong>Input Layer:</strong> 12 features (player stats: PTS, AST, REB, efficiency metrics, physical attributes)</p>
              <p><strong>Hidden Layers:</strong> 32 neurons → 16 neurons (ReLU activation)</p>
              <p><strong>Output Layer:</strong> 1 neuron (Sigmoid activation for binary classification)</p>

              <h3>Training Process</h3>
              <ul>
                <li><strong>Forward Propagation:</strong> Pass player stats through the network</li>
                <li><strong>Error Calculation:</strong> Compare predicted vs. actual optimal player labels</li>
                <li><strong>Backpropagation:</strong> Adjust weights to minimize error</li>
                <li><strong>Optimization:</strong> 1000 epochs with 0.01 learning rate</li>
              </ul>

              <h3>Team Selection Logic</h3>
              <p>The system enforces position requirements (PG, SG, SF, PF, C) while maximizing the selected score (Overall/Offense/Defense).
              If a position has no perfect candidates, it intelligently falls back to position families (e.g., SG → PG, PF → C).</p>
            </div>
          </div>
        </>
      )}
    </div>
    </>
  );
}

export default NBATeam;
